import { loadApprovedTemplates } from "./loadApprovedTemplates";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { useAuthContext } from "../../../Components/AuthContext";
import {
  Alert,
  Badge,
  Button,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "../../../Components/ui";
import ToggleSwitch from "../../../Components/ui/ToggleSwitch";
import {
  AlimTalkAccountMode,
  AlimTalkTemplate,
  CategorySetting,
  EventPolicy,
  EventPolicyUpdatePayload,
  NotificationCategory,
  NotificationChannel,
  NotificationEventCatalogItem,
  notificationCenterService,
} from "../../../api/services/notification-center.service";
import { hasCapability } from "../../../common/permissions";
import {
  getAlimTalkTemplateName,
  getAlimTalkTemplateVariables,
  normalizeAlimTalkAccountMode,
} from "./alimtalkTemplate";

const categoryMeta: Array<{ value: NotificationCategory; label: string; description: string }> = [
  { value: "payment", label: "결제 관련", description: "결제 완료·실패·취소·환불 안내" },
  { value: "usage", label: "이용관련", description: "예약·입장·티켓 및 바우처 이용 안내" },
  { value: "site_notice", label: "지점 공지", description: "운영시간·휴장·시설 및 현장 안내" },
  { value: "marketing", label: "마케팅", description: "혜택·쿠폰·이벤트·프로모션 안내" },
  { value: "mandatory", label: "필수 고지", description: "보안·약관·법적 고지" },
];

const channelLabel: Record<NotificationChannel, string> = {
  web_push: "Web Push",
  app_push: "App Push",
  alimtalk: "알림톡",
};

const variableKeys = (variables: NotificationEventCatalogItem["variables"] = []) => variables
  .map((variable) => typeof variable === "string" ? variable : variable.key)
  .filter(Boolean);

const defaultPolicyFor = (catalog: NotificationEventCatalogItem): EventPolicy => ({
  event_type: catalog.event_type,
  category: catalog.category,
  is_enabled: catalog.default_policy?.is_enabled ?? true,
  web_push_enabled: catalog.default_policy?.web_push_enabled ?? true,
  app_push_enabled: catalog.default_policy?.app_push_enabled ?? true,
  alimtalk_enabled: catalog.default_policy?.alimtalk_enabled ?? false,
  alimtalk_template_id: catalog.default_policy?.alimtalk_template_id ?? null,
  alimtalk_template_name: catalog.default_policy?.alimtalk_template_name ?? "",
  alimtalk_variable_bindings: catalog.default_policy?.alimtalk_variable_bindings ?? {},
  channel_priority: catalog.default_policy?.channel_priority ?? ["web_push", "app_push", "alimtalk"],
  alimtalk_template_revision: catalog.default_policy?.alimtalk_template_revision,
});

type SystemMessageSettingsProps = {
  view?: "site" | "headquarters";
  accountModeOverride?: AlimTalkAccountMode;
};

export default function SystemMessageSettings({
  view = "site",
  accountModeOverride,
}: SystemMessageSettingsProps) {
  const { userInfo } = useAuthContext();
  const isHeadquartersView = view === "headquarters";
  const canRead = isHeadquartersView
    ? userInfo?.is_hq_admin === true
    : Boolean(userInfo?.is_global_admin || hasCapability(userInfo, "site_manager"));
  const [resolvedAccountMode, setResolvedAccountMode] = useState<AlimTalkAccountMode | null>(null);
  const [settings, setSettings] = useState<CategorySetting[]>([]);
  const [catalog, setCatalog] = useState<NotificationEventCatalogItem[]>([]);
  const [policies, setPolicies] = useState<EventPolicy[]>([]);
  const [templates, setTemplates] = useState<AlimTalkTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateApiAvailable, setTemplateApiAvailable] = useState(true);
  const canEdit = isHeadquartersView
    ? userInfo?.is_hq_admin === true && resolvedAccountMode === "HEADQUARTERS"
    : resolvedAccountMode === "DIRECT" && hasCapability(userInfo, "site_owner");

  const load = useCallback(async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [settingsResult, accountResult] = await Promise.allSettled([
      notificationCenterService.getSiteSettings(),
      accountModeOverride === undefined
        ? notificationCenterService.getAlimTalkAccount()
        : Promise.resolve(null),
    ]);
    if (settingsResult.status === "fulfilled") {
      const settingsResponse = settingsResult.value;
      const responseCatalog = Array.isArray(settingsResponse.data.event_catalog)
        ? settingsResponse.data.event_catalog
        : [];
      const responsePolicies = Array.isArray(settingsResponse.data.event_policies)
        ? settingsResponse.data.event_policies
        : [];
      setSettings(Array.isArray(settingsResponse.data.settings) ? settingsResponse.data.settings : []);
      setCatalog(responseCatalog.length ? responseCatalog : responsePolicies.map((policy) => ({
        event_type: policy.event_type,
        label: policy.event_type,
        category: policy.category,
      })));
      setPolicies(responsePolicies);
    } else {
      toast.error(settingsResult.reason?.message || "시스템 메시지 설정을 불러오지 못했습니다.");
    }
    setResolvedAccountMode(accountModeOverride ?? (
      accountResult.status === "fulfilled" && accountResult.value
        ? normalizeAlimTalkAccountMode(accountResult.value.data.account_mode, accountResult.value.data.using_default)
        : null
    ));

    try {
      const approvedTemplates = await loadApprovedTemplates(notificationCenterService.getAlimTalkTemplates);
      setTemplates(approvedTemplates);
      setTemplateApiAvailable(true);
    } catch {
      setTemplates([]);
      setTemplateApiAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [accountModeOverride, canRead]);

  useEffect(() => {
    void load();
  }, [load]);

  const effectivePolicies = useMemo(() => catalog.map((item) => {
    const existing = policies.find((policy) => policy.event_type === item.event_type);
    return existing ? { ...defaultPolicyFor(item), ...existing } : defaultPolicyFor(item);
  }), [catalog, policies]);

  const updateCategory = (category: NotificationCategory, field: keyof CategorySetting, value: boolean) => {
    setSettings((current) => current.map((setting) => (
      setting.category === category ? { ...setting, [field]: value } : setting
    )));
  };

  const updatePolicy = (eventType: string, updates: Partial<EventPolicy>) => {
    const catalogItem = catalog.find((item) => item.event_type === eventType);
    if (!catalogItem) return;
    setPolicies((current) => {
      const existing = current.find((policy) => policy.event_type === eventType) || defaultPolicyFor(catalogItem);
      const next = { ...existing, ...updates };
      return current.some((policy) => policy.event_type === eventType)
        ? current.map((policy) => policy.event_type === eventType ? next : policy)
        : [...current, next];
    });
  };

  const selectTemplate = (policy: EventPolicy, templateId: number | null) => {
    const template = templates.find((item) => item.id === templateId) || null;
    const event = catalog.find((item) => item.event_type === policy.event_type);
    const availableEventVariables = new Set(variableKeys(event?.variables));
    const bindings = Object.fromEntries(getAlimTalkTemplateVariables(template).map((variable) => [
      variable.key,
      availableEventVariables.has(variable.key) ? variable.key : "",
    ]));
    updatePolicy(policy.event_type, {
      alimtalk_template_id: template?.id || null,
      alimtalk_template_name: getAlimTalkTemplateName(template),
      alimtalk_variable_bindings: bindings,
      alimtalk_enabled: Boolean(template),
    });
  };

  const save = async () => {
    if (!canEdit) return;
    if (effectivePolicies.some((policy) => policy.alimtalk_template_id && !templates.some((template) => template.id === policy.alimtalk_template_id))) {
      toast.error("사용할 수 없는 연결 템플릿이 있습니다. 승인 템플릿을 다시 선택하거나 연결을 해제해 주세요.");
      return;
    }
    const incompleteBinding = effectivePolicies.find((policy) => (
      policy.alimtalk_template_id
      && Object.values(policy.alimtalk_variable_bindings || {}).some((value) => !value)
    ));
    if (incompleteBinding) {
      toast.error("알림톡 템플릿의 모든 변수를 시스템 데이터에 연결해 주세요.");
      return;
    }
    setSaving(true);
    try {
      const eventPolicies: EventPolicyUpdatePayload[] = effectivePolicies.map((policy) => ({
        event_type: policy.event_type,
        category: policy.category,
        is_enabled: policy.is_enabled,
        web_push_enabled: policy.web_push_enabled,
        app_push_enabled: policy.app_push_enabled,
        alimtalk_enabled: policy.alimtalk_enabled,
        alimtalk_template_id: policy.alimtalk_template_id,
        alimtalk_variable_bindings: policy.alimtalk_variable_bindings || {},
        channel_priority: policy.channel_priority,
      }));
      const response = await notificationCenterService.updateSiteSettings({ settings, event_policies: eventPolicies });
      setSettings(response.data.settings || settings);
      setPolicies(response.data.event_policies || effectivePolicies);
      if (Array.isArray(response.data.event_catalog)) setCatalog(response.data.event_catalog);
      toast.success("시스템 메시지 설정을 저장했습니다.");
    } catch (error: any) {
      toast.error(error?.message || "시스템 메시지 설정 저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  if (!canRead) {
    return <Alert color="warning">시스템 메시지 설정을 확인할 권한이 없습니다.</Alert>;
  }
  if (loading) return <div className="text-center py-4"><Spinner size="sm" className="me-2" />시스템 메시지 설정을 불러오고 있습니다.</div>;

  return (
    <div>
      {!canEdit ? <Alert color="info">{resolvedAccountMode === "HEADQUARTERS" ? "본사 계정의 시스템 메시지 연결은 HQ 관리자 페이지에서만 변경할 수 있습니다." : isHeadquartersView ? "현재 지점은 직접 계정을 사용 중입니다. 본사 공용 계정으로 전환한 뒤 설정할 수 있습니다." : "시스템 메시지 설정은 지점주만 변경할 수 있습니다."}</Alert> : null}
      <div className="mb-3"><h5 className="mb-1">{isHeadquartersView ? "본사 분류별 채널 설정" : "분류별 채널 설정"}</h5><p className="text-muted mb-0">고객 알림 분류와 기본 발송 채널을 설정합니다.</p></div>
      <Row className="g-3">
        {categoryMeta.map((meta) => {
          const setting = settings.find((item) => item.category === meta.value);
          if (!setting) return null;
          const mandatory = meta.value === "mandatory";
          return (
            <Col lg={6} key={meta.value}>
              <div className="border rounded p-3 h-100">
                <div className="d-flex justify-content-between gap-3 mb-2"><div><h6 className="mb-1">{meta.label}</h6><p className="text-muted small mb-0">{meta.description}</p></div><ToggleSwitch checked={setting.is_enabled} disabled={!canEdit || mandatory} onChange={(value) => updateCategory(meta.value, "is_enabled", value)} /></div>
                <div className="d-flex flex-wrap gap-3 mt-3">{(Object.keys(channelLabel) as NotificationChannel[]).map((channel) => {
                  const field = `${channel}_enabled` as keyof CategorySetting;
                  return <ToggleSwitch key={channel} label={channelLabel[channel]} checked={Boolean(setting[field])} disabled={!canEdit || (mandatory && channel !== "alimtalk")} onChange={(value) => updateCategory(meta.value, field, value)} />;
                })}</div>
              </div>
            </Col>
          );
        })}
      </Row>

      <div className="mt-4 mb-3"><h5 className="mb-1">시스템 메시지별 알림톡 연결</h5><p className="text-muted mb-0">자동 발송 이벤트에 승인된 알림톡 템플릿과 변수 데이터를 연결합니다.</p></div>
      {!templateApiAvailable ? <Alert color="warning">알림톡 템플릿 API가 아직 준비되지 않아 템플릿 연결을 변경할 수 없습니다.</Alert> : null}
      {catalog.length === 0 ? <Alert color="info">연결 가능한 시스템 메시지 이벤트가 아직 등록되지 않았습니다.</Alert> : null}
      {catalog.length ? (
        <div className="table-responsive border rounded">
          <Table className="align-middle mb-0 small">
            <thead><tr><th>시스템 메시지</th><th>분류</th><th>사용</th><th>알림톡</th><th style={{ minWidth: 240 }}>승인 템플릿</th><th style={{ minWidth: 280 }}>변수 연결</th></tr></thead>
            <tbody>
              {effectivePolicies.map((policy) => {
                const item = catalog.find((catalogItem) => catalogItem.event_type === policy.event_type);
                const template = templates.find((candidate) => candidate.id === policy.alimtalk_template_id) || null;
                const eventVariables = variableKeys(item?.variables);
                return (
                  <tr key={policy.event_type}>
                    <td><strong>{item?.label || policy.event_type}</strong><div className="text-muted">{item?.description || policy.event_type}</div></td>
                    <td><Badge color="light">{categoryMeta.find((meta) => meta.value === policy.category)?.label || policy.category}</Badge></td>
                    <td><ToggleSwitch checked={policy.is_enabled} disabled={!canEdit} onChange={(value) => updatePolicy(policy.event_type, { is_enabled: value })} /></td>
                    <td><ToggleSwitch checked={policy.alimtalk_enabled} disabled={!canEdit || !template} onChange={(value) => updatePolicy(policy.event_type, { alimtalk_enabled: value })} /></td>
                    <td>
                      <Input aria-label={`${item?.label || policy.event_type} 승인 템플릿`} type="select" value={template?.id || ""} disabled={!canEdit || !templateApiAvailable} onChange={(event) => selectTemplate(policy, event.target.value ? Number(event.target.value) : null)}>
                        <option value="">연결 안 함</option>
                        {templates.map((candidate) => <option value={candidate.id} key={candidate.id}>{getAlimTalkTemplateName(candidate)} ({candidate.template_code || candidate.id})</option>)}
                      </Input>
                      {policy.alimtalk_template_id && !template ? <div className="text-warning mt-1">기존 연결 템플릿을 사용할 수 없습니다. 승인 템플릿을 다시 선택해 주세요.{canEdit ? <Button type="button" color="link" size="sm" onClick={() => selectTemplate(policy, null)}>연결 해제</Button> : null}</div> : null}
                    </td>
                    <td>
                      {template ? getAlimTalkTemplateVariables(template).map((variable) => (
                        <div key={variable.key} className="d-flex align-items-center gap-2 mb-1">
                          <code style={{ minWidth: 110 }}>{variable.key}</code>
                          <Input type="select" value={policy.alimtalk_variable_bindings?.[variable.key] || ""} disabled={!canEdit} onChange={(event) => updatePolicy(policy.event_type, { alimtalk_variable_bindings: { ...(policy.alimtalk_variable_bindings || {}), [variable.key]: event.target.value } })}>
                            <option value="">선택 필요</option>
                            {eventVariables.map((eventVariable) => <option key={eventVariable} value={eventVariable}>{eventVariable}</option>)}
                          </Input>
                        </div>
                      )) : <span className="text-muted">템플릿을 선택해 주세요.</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      ) : null}
      {canEdit ? <div className="d-flex justify-content-end mt-4"><Button color="primary" onClick={() => void save()} disabled={saving}>{saving ? <><Spinner size="sm" className="me-2" />저장 중</> : "설정 저장"}</Button></div> : null}
    </div>
  );
}
